import {
  createContext,
  useContext,
  useLayoutEffect,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { ClerkProvider, SignInButton, UserButton, useAuth, useUser } from "@clerk/react";
import { esES } from "@clerk/localizations";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import Box from "@mui/material/Box";
import { configureSourceSession } from "@/yomiri/core/SourceSession.ts";
import { AniListSettings } from '../ui/components/AniListSettings.tsx';
import SyncOutlined from '@mui/icons-material/SyncOutlined';

import './account.css';

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
interface SessionState { ready: boolean; signedIn: boolean; enabled: boolean; name?: string; email?: string; image?: string; userId?: string }
const Session = createContext<SessionState>({ ready: true, signedIn: false, enabled: false });
export const useYomiriSession = () => useContext(Session);

function SessionBridge({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, userId, getToken } = useAuth();
  const { user } = useUser();
  useLayoutEffect(() => {
    if (isLoaded) configureSourceSession(isSignedIn ? userId : null, getToken);
  }, [isLoaded, isSignedIn, userId, getToken]);
  return (
    <Session.Provider value={{ ready: isLoaded, signedIn: !!isSignedIn, enabled: true, userId: userId || undefined, name: user?.fullName || user?.username || undefined, email: user?.primaryEmailAddress?.emailAddress, image: user?.imageUrl }}>
      {children}
    </Session.Provider>
  );
}

export function YomiriAuthProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  useLayoutEffect(() => {
    if (!online) configureSourceSession(null);
  }, [online]);
  if (!publishableKey || !online)
    return (
      <Session.Provider value={{ ready: true, signedIn: false, enabled: false }}>
        {children}
      </Session.Provider>
    );
  return (
    <ClerkProvider
      publishableKey={publishableKey}
      localization={esES}
      appearance={{
        variables: {
          colorPrimary: "#ffb1c4",
          colorBackground: "#252124",
          colorForeground: "#eee0e5",
          colorMutedForeground: "#cdbfc5",
          colorInput: "#302b2e",
          colorInputForeground: "#eee0e5",
          colorNeutral: "#ece8e6",
          colorBorder: "#4d4147",
          borderRadius: "16px",
          fontFamily: "system-ui",
        },
      }}
    >
      <SessionBridge>{children}</SessionBridge>
    </ClerkProvider>
  );
}

export function YomiriAccount() {
  const session = useYomiriSession();
  if (!session.enabled || !session.ready) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
      {session.signedIn ? (
        <UserButton>
          <UserButton.MenuItems><UserButton.Action label="Conexiones" labelIcon={<SyncOutlined />} open="conexiones" /></UserButton.MenuItems>
          <UserButton.UserProfilePage label="Conexiones" url="conexiones" labelIcon={<SyncOutlined />}><AniListSettings /></UserButton.UserProfilePage>
        </UserButton>
      ) : (
        <SignInButton mode="modal" forceRedirectUrl={location.pathname + location.search}>
          <button type="button" className="y-account-signin"><PersonOutlineRounded aria-hidden="true" />Iniciar sesión</button>
        </SignInButton>
      )}
    </Box>
  );
}
